import { SEXES } from 'constants/constants';
import { get } from 'lodash';
import * as yup from 'yup';

function capitalizeFirstLetter(str: string) {
  return str[0].toUpperCase() + str.slice(1);
}

export const createYupSchemaFromConfig = (
  config: any,
  t: any,
  portalSetting: any,
): yup.ObjectSchema<any> =>
  yup.object().shape(
    Object.keys(config).reduce((schema: { [key: string]: yup.AnySchema }, key: string) => {
      let fieldSchema: yup.AnySchema | null;

      // Check if the current config[key] is a nested object (doesn't have a 'type' property)
      if (typeof config[key] === 'object' && config[key] && !config[key]?.hasOwnProperty('type')) {
        // This is a nested object, recursively create schema for it
        // test physicians portal config to see if it should be included
        if (config[key]?.hasOwnProperty('checkPortalSettings')) {
          if (get(portalSetting, `see${capitalizeFirstLetter(key)}`, false)) {
            fieldSchema = createYupSchemaFromConfig(config[key], t, portalSetting);
          } else {
            fieldSchema = null;
          }
        } else {
          fieldSchema = createYupSchemaFromConfig(config[key], t, portalSetting);
        }

        // Make nested objects nullable and optional if they are not required
        if (fieldSchema) {
          fieldSchema = fieldSchema.nullable().optional();
        }
      } else {
        // This is a field with a type, create schema based on type
        switch (config[key]?.type) {
          case 'text':
            fieldSchema = yup.string();
            break;
          case 'date':
            fieldSchema = yup.date();
            break;
          case 'time':
            fieldSchema = yup.string().matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, t('time'));
            break;
          case 'timeWithSeconds':
            fieldSchema = yup
              .string()
              .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/, t('timeWithSecond'));
            break;
          case 'select':
            fieldSchema = yup.string();
            break;
          default:
            fieldSchema = yup.object().nullable();
        }

        // Apply validation rules
        //required
        if (config[key]?.required) {
          fieldSchema = fieldSchema.required(t('required'));
        }

        //min string length
        if (config[key]?.min && config[key]?.type === 'text') {
          fieldSchema = (fieldSchema as yup.StringSchema)
            //convert empty string to null
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .when([], {
              is: () => config[key]?.required,
              then: (schema) =>
                (schema as yup.StringSchema).min(
                  config[key].min!,
                  t('minLength2', { count: config[key].min }),
                ),
              otherwise: (schema) =>
                schema
                  .min(config[key].min!, t('minLength2', { count: config[key].min }))
                  .notRequired(),
            });
        }
        //max string length
        if (config[key]?.max && config[key]?.type === 'text') {
          fieldSchema = (fieldSchema as yup.StringSchema)
            //convert empty string to null
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .when([], {
              is: () => config[key]?.required,
              then: (schema) =>
                (schema as yup.StringSchema).max(
                  config[key].max!,
                  t('maxLength2', { count: config[key].max }),
                ),
              otherwise: (schema) =>
                schema
                  .max(config[key].max!, t('maxLength2', { count: config[key].max }))
                  .notRequired(),
            });
        }
        // future date not allowed
        if (config[key]?.futureNotAllowed && config[key]?.type === 'date') {
          fieldSchema = (fieldSchema as yup.DateSchema).max(new Date(), t('dateInFuture'));
        }
      }
      return fieldSchema ? { ...schema, [key]: fieldSchema } : { ...schema };
    }, {}),
  );

export const createFieldsFromConfig = (
  config: any,
  t: any,
  basePath: string = '',
  baseSection: string = '',
): any[] => {
  const fields: any[] = [];

  Object.keys(config).forEach((key: string) => {
    const currentPath = basePath ? `${basePath}.${key}` : key;
    const currentSection = config[key]?.section || baseSection || 'default';

    // Check if this is a field with a type property
    if (config[key]?.hasOwnProperty('type')) {
      const fieldConfig = config[key];

      // Create base field configuration
      let field: any = {
        name: currentPath,
        label: t(fieldConfig.label || key), // You might want to customize this based on your translation keys
        required: fieldConfig.required || false,
        section: currentSection, // Add section property
      };

      // Add type-specific configurations
      switch (fieldConfig.type) {
        case 'text':
          field.type = 'text';
          field.inputProps = {};
          if (fieldConfig.max) {
            field.inputProps.maxLength = fieldConfig.max;
          }
          if (fieldConfig.min) {
            field.inputProps.minLength = fieldConfig.min;
          }
          if (fieldConfig.allowedChars) {
            field.pattern = fieldConfig.allowedChars;
          }
          break;

        case 'date':
          field.type = 'date';
          break;

        case 'time':
          field.type = 'time';
          field.placeholder = 'hh:mm';
          field.inputProps = { maxLength: 5 };
          break;

        case 'select':
          field.type = 'select';
          field.inputProps = {};

          if (fieldConfig.items) {
            field.items = fieldConfig.items;
          } else if (key === 'sex') {
            field.items = SEXES.map((sex) => ({ id: sex.value, label: t(sex.label) }));
          } else {
            field.items = [];
          }
          break;

        default:
          field = null;
      }
      if (field) {
        fields.push(field);
      }
    } else if (typeof config[key] === 'object' && config[key]) {
      // This is a nested object, recurse into it
      fields.push(...createFieldsFromConfig(config[key], t, currentPath, currentSection));
    }
  });

  return fields;
};

export const groupFieldsBySection = (fields: any[], t: any, portalSetting: any) => {
  const sectionsMap: { [key: string]: { id: string; label?: string; fields: any[] } } = {};

  fields.forEach((field) => {
    const section = field.section || 'default';
    if (!sectionsMap[section]) {
      sectionsMap[section] = { id: section, fields: [] };
    }
    sectionsMap[section].fields.push({ ...field, section: undefined }); // Remove section from field object
  });

  //check if phycisians sections should be included based on portal settings
  ['referringPhysician', 'performingPhysician', 'requestingPhysician'].forEach(
    (physician: string) => {
      if (!get(portalSetting, `see${capitalizeFirstLetter(physician)}`, false)) {
        delete sectionsMap[physician];
      } else {
        sectionsMap[physician].label = t(physician);
      }
    },
  );

  // Convert the map to an array of section objects
  const groupedFields: { id: string; label?: string; fields: any[] }[] = Object.values(sectionsMap);

  return groupedFields;
};
